import { Component } from '@theme/component';

/**
 * Updates the visual gift availability for the selected product set.
 *
 * @typedef {object} ProductFreeGiftsRefs
 * @property {HTMLElement[]} gift
 * @property {HTMLParagraphElement} summary
 * @extends Component<ProductFreeGiftsRefs>
 */
class ProductFreeGifts extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    const details = this.closest('.product-details');
    details?.addEventListener('change', this.#handleVariantChange, {
      signal: this.#abortController.signal,
    });

    const selectedOption = details?.querySelector('variant-picker input:checked, variant-picker option:checked');
    const setSize = Number.parseInt(
      selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement
        ? selectedOption.value
        : '',
      10
    );
    this.#updateGifts(Number.isNaN(setSize) ? 7 : setSize);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /** @param {Event} event */
  #handleVariantChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;
    if (!target.closest('variant-picker')) return;

    const setSize = Number.parseInt(target.value, 10);
    if (Number.isNaN(setSize)) return;

    this.#updateGifts(setSize);
  };

  /** @param {number} setSize */
  #updateGifts(setSize) {
    const unlockedCount = setSize >= 10 ? 4 : setSize >= 7 ? 1 : 0;

    for (const gift of this.refs.gift ?? []) {
      const giftIndex = Number(gift.dataset.giftIndex);
      const isLocked = giftIndex > unlockedCount;
      gift.classList.toggle('is-locked', isLocked);
      if (isLocked) {
        gift.setAttribute('aria-disabled', 'true');
      } else {
        gift.removeAttribute('aria-disabled');
      }
    }

    if (setSize >= 10) {
      this.refs.summary.textContent = 'All 4 gifts unlocked.';
    } else if (setSize >= 7) {
      this.refs.summary.textContent = 'Wooden holder unlocked. Three more gifts unlock with the 10-piece set.';
    } else {
      this.refs.summary.textContent = 'Choose the 7-piece or 10-piece set to unlock free gifts.';
    }
  }
}

/**
 * Runs the duration-based promotional countdown.
 *
 * @typedef {object} OfferCountdownRefs
 * @property {HTMLTimeElement} countdown
 * @extends Component<OfferCountdownRefs>
 */
class OfferCountdown extends Component {
  requiredRefs = ['countdown'];

  /** @type {number | undefined} */
  #interval;

  connectedCallback() {
    super.connectedCallback();
    this.#render();
    this.#interval = window.setInterval(() => this.#render(), 1000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#interval) window.clearInterval(this.#interval);
  }

  #getDeadline() {
    const duration = Math.max(1, Number(this.dataset.durationMinutes) || 19) * 60 * 1000;
    const storageKey = this.dataset.storageKey || 'product-offer-countdown';
    const storedDeadline = Number(sessionStorage.getItem(storageKey));
    if (storedDeadline > Date.now()) return storedDeadline;

    const deadline = Date.now() + duration;
    sessionStorage.setItem(storageKey, String(deadline));
    return deadline;
  }

  #render() {
    const remaining = Math.max(0, this.#getDeadline() - Date.now());
    const totalSeconds = Math.ceil(remaining / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const text = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    this.refs.countdown.textContent = text;
    this.refs.countdown.dateTime = `PT${minutes}M${seconds}S`;
  }
}

if (!customElements.get('offer-countdown')) {
  customElements.define('offer-countdown', OfferCountdown);
}

if (!customElements.get('product-free-gifts')) {
  customElements.define('product-free-gifts', ProductFreeGifts);
}
